// Convierte una marca de tiempo recibida del backend en un objeto Date.
// Los LocalDateTime del backend llegan sin zona (ej: "2026-05-04T23:15:00") pero en realidad
// están almacenados en UTC, por lo que añadimos la 'Z' antes de parsear.
export const parseBackendDate = (rawValue) => {
  if (!rawValue) return null;
  let value = rawValue;
  if (typeof value === 'string' && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(value)) {
    value = value + 'Z';
  }
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
};

// Devuelve los componentes year/month/day/hour/minute de una fecha expresada en una TZ concreta.
export const getZonedParts = (date, timeZone) => {
  try {
    const dtf = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const parts = dtf.formatToParts(date).reduce((acc, p) => {
      if (p.type !== 'literal') acc[p.type] = p.value;
      return acc;
    }, {});
    return {
      year: Number(parts.year),
      month: Number(parts.month),
      day: Number(parts.day),
      hour: Number(parts.hour === '24' ? '00' : parts.hour),
      minute: Number(parts.minute),
    };
  } catch (e) {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
      hour: date.getHours(),
      minute: date.getMinutes(),
    };
  }
};

// Formatea la marca de tiempo del último mensaje para mostrarla en cada chat-item,
// teniendo en cuenta la zona horaria del bot.
// - Hoy: HH:MM
// - Ayer: "Ayer"
// - Misma semana: nombre del día (Lun, Mar, ...)
// - Resto: dd/mm/yy
export const formatLastMessageAt = (rawValue, timeZone) => {
  const date = parseBackendDate(rawValue);
  if (!date) return '';

  const tz = timeZone || 'Europe/Madrid';
  const pad = (n) => String(n).padStart(2, '0');

  const dateParts = getZonedParts(date, tz);
  const nowParts = getZonedParts(new Date(), tz);

  const dateUTC = Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day);
  const nowUTC = Date.UTC(nowParts.year, nowParts.month - 1, nowParts.day);
  const diffDays = Math.round((nowUTC - dateUTC) / 86400000);

  if (diffDays <= 0) {
    return `${pad(dateParts.hour)}:${pad(dateParts.minute)}`;
  }
  if (diffDays === 1) {
    return 'Ayer';
  }
  if (diffDays < 7) {
    try {
      const weekday = new Intl.DateTimeFormat('es-ES', { weekday: 'short', timeZone: tz })
        .format(date)
        .replace('.', '');
      return weekday.charAt(0).toUpperCase() + weekday.slice(1);
    } catch (e) {
      const dias = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
      return dias[date.getDay()];
    }
  }
  const yy = String(dateParts.year).slice(-2);
  return `${pad(dateParts.day)}/${pad(dateParts.month)}/${yy}`;
};

// Texto completo (día y hora) que se muestra como tooltip en el chat-item,
// también en la zona horaria del bot.
export const formatLastMessageAtFull = (rawValue, timeZone) => {
  const date = parseBackendDate(rawValue);
  if (!date) return '';
  const tz = timeZone || 'Europe/Madrid';
  const pad = (n) => String(n).padStart(2, '0');
  const p = getZonedParts(date, tz);
  return `${pad(p.day)}/${pad(p.month)}/${p.year} ${pad(p.hour)}:${pad(p.minute)}`;
};
